import React from 'react';
import { useTranslation } from 'react-i18next';
import { Apple, Download, Eye, EyeOff, Lock, Mail, Play, Shield, Smartphone } from 'lucide-react';

export type FieldTeamLoginCardProps = {
  email: string;
  password: string;
  showPassword: boolean;
  loading: boolean;
  storeLinks: { playStoreUrl?: string; appStoreUrl?: string };
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onTogglePassword: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onOpenSignup: () => void;
};

export const FieldTeamLoginCard: React.FC<FieldTeamLoginCardProps> = ({
  email,
  password,
  showPassword,
  loading,
  storeLinks,
  onEmailChange,
  onPasswordChange,
  onTogglePassword,
  onSubmit,
  onOpenSignup,
}) => {
  const { t } = useTranslation();
  const roleLabels = {
    client: t('crmRoleClient'),
    crm_manager: t('crmRoleManager'),
    sales_rep: t('crmRoleSalesRep'),
  } as const;

  return (
    <div className="ftl-login-card">
      <div className="dcl-section-badge">
        <Shield size={14} />
        {t('crmLoginSecureLogin')}
      </div>
      <h2 className="dcl-section-title" style={{ fontSize: 28, marginBottom: 8 }}>
        {t('crmLoginWelcomeBack')}
      </h2>
      <p className="dcl-section-sub" style={{ marginBottom: 20 }}>{t('crmLoginSignInDesc')}</p>

      <div className="ftl-role-row">
        {(['client', 'crm_manager', 'sales_rep'] as const).map((role) => (
          <span key={role} className="ftl-role-pill">
            <span
              className="ftl-role-dot"
              style={{
                background: role === 'client' ? '#10B981' : role === 'crm_manager' ? '#F59E0B' : '#2563EB',
              }}
            />
            {roleLabels[role]}
          </span>
        ))}
      </div>

      <form className="dcl-form" onSubmit={onSubmit}>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
            {t('crmLoginEmailLabel')}
          </label>
          <div className="ftl-input-wrap">
            <Mail size={16} />
            <input
              type="email"
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              placeholder={t('crmLoginEmailPlaceholder')}
              className="dcl-input"
              autoComplete="email"
              required
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
            {t('crmLoginPasswordLabel')}
          </label>
          <div className="ftl-input-wrap">
            <Lock size={16} />
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => onPasswordChange(e.target.value)}
              placeholder={t('crmLoginPasswordPlaceholder')}
              className="dcl-input"
              autoComplete="current-password"
              required
            />
            <button type="button" className="ftl-eye" onClick={onTogglePassword} aria-label={t('crmLoginPasswordLabel')}>
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
        <button type="submit" className="dcl-btn dcl-btn--primary dcl-btn--block" disabled={loading}>
          {loading ? t('crmLoginSigningIn') : t('crmLoginSubmit')}
        </button>
      </form>

      <div className="mt-5 space-y-3">
        <a href="/download" className="dcl-btn dcl-btn--secondary dcl-btn--block">
          <Smartphone size={16} />
          {t('crmLoginDownloadApp')}
          <Download size={16} />
        </a>
        {(storeLinks.playStoreUrl || storeLinks.appStoreUrl) && (
          <div className="ftl-store-row">
            {storeLinks.playStoreUrl && (
              <a href={storeLinks.playStoreUrl} target="_blank" rel="noopener noreferrer" className="dcl-btn dcl-btn--ghost dcl-btn--sm">
                <Play size={14} />
                {t('crmLoginGetOnGooglePlay')}
              </a>
            )}
            {storeLinks.appStoreUrl && (
              <a href={storeLinks.appStoreUrl} target="_blank" rel="noopener noreferrer" className="dcl-btn dcl-btn--ghost dcl-btn--sm">
                <Apple size={14} />
                {t('crmLoginGetOnAppStore')}
              </a>
            )}
          </div>
        )}
        <p className="text-center text-xs text-slate-500">{t('crmLoginDownloadAppHint')}</p>
        <p className="text-center text-sm text-slate-500">
          {t('crmLoginNewUser')}{' '}
          <button type="button" onClick={onOpenSignup} className="text-blue-600 font-semibold">
            {t('crmLoginSignUp')}
          </button>
        </p>
        <p className="text-center text-xs text-slate-400">{t('crmLoginPlatinumIncluded')}</p>
      </div>
    </div>
  );
};

export default FieldTeamLoginCard;
